import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PayloadListEmployeeType, PayloadMutationEmployeeType } from 'components/types/employees/detail';
import { useDispatch } from 'react-redux';
import { HIT_BOARD_STATUS } from 'redux/actions/board';
import { DO_LOADING } from 'redux/actions/loading';
import { apiEmployee, employeeService } from './employee.service';

interface ErrorMessageImportType {
  message: string,
  raw?: {
    data: { no: number, row: string, message: string }[],
    error: boolean,
    errorMessage: [],
    message: string,
    status: number
  },
}

export const useEmployeeList = ({
  board_id,
  status = 1,
  search,
  limit,
  offset,
  enabled = true,
}: PayloadListEmployeeType) => useQuery({
  queryKey: [apiEmployee.listEmployee, board_id, search, limit, offset],
  queryFn: () => employeeService.getEmployee({
    board_id,
    status,
    limit,
    offset,
    search,
  }),
  enabled: !!board_id && !!status && enabled,
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
});

export const useChartEmployee = (boardId: null | string, enabled :boolean = true) => useQuery({
  queryKey: [apiEmployee.chartEmployee, boardId],
  queryFn: () => employeeService.getChartEmployee({
    board_id: boardId,
  }),
  enabled: !!boardId && enabled,
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
});



export const useUploadPicture = () => {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payloadFile: any) => employeeService.uploadProfile(payloadFile),
    onError: (error) => {
      const errorMessage = error.message;
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'failed',
          message: errorMessage,
        },
      });
    },
  });
};

const useMutationCategoryEmployee = <TPayload, TResponse extends { message: string }> (mutationFn: (payload:TPayload) => Promise<TResponse>) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (responseData: TResponse) => {
      const successMessage = responseData.message;

      void queryClient.invalidateQueries({
        queryKey: [apiEmployee.listEmployee],
      });
      void queryClient.invalidateQueries({
        queryKey: [apiEmployee.chartEmployee],
      });

      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'success',
          message: successMessage,
        },
      });
    },
    onError: (error) => {
      const errorMessage = error.message;
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'failed',
          message: errorMessage,
        },
      });
    },
  });
};

export const useCreateEmployee = () => useMutationCategoryEmployee(employeeService.createEmployee);

export const useUpdateEmployee = () => useMutationCategoryEmployee(employeeService.updateEmployee);

export const useImportUpdateEmployee = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payloadImport: PayloadMutationEmployeeType) => employeeService.importUpdateEmployee(payloadImport),
    onMutate: () => {
      dispatch({ type: DO_LOADING, payload: true });
    },
    onSuccess: (responseData) => {
      const successMessage = responseData.message;
      void queryClient.invalidateQueries({
        queryKey: [apiEmployee.listEmployee],
      });
      void queryClient.invalidateQueries({
        queryKey: [apiEmployee.chartEmployee],
      });

      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'success',
          message: successMessage,
        },
      });
    },
    onError: (error: ErrorMessageImportType) => {
      const errorMessage = error.raw?.data || {};
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'failed',
          tempData: errorMessage,
          message: error.message,
          listView: Object.keys(errorMessage).length !== 0 ? 'listView' : '',
        },
      });
    },
    onSettled: () => {
      dispatch({ type: DO_LOADING, payload: false });
    },
  });
};
